import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, ButtonGroup, Input, InputGroup, Message, Panel, SelectPicker } from 'rsuite';
import * as XLSX from 'xlsx';
import LoadingSpinner from '../components/LoadingSpinner';
import PageHeader from '../components/shared/PageHeader';
import { useAllResumes, useCompanyDirectory } from '../hooks/useQueries';
import './ResumeAdminPage.css';

const CATEGORIES = {
  experiences: {
    label: 'Experience',
    columns: [
      { key: 'employee_name', label: 'Employee' },
      { key: 'email', label: 'Email' },
      { key: 'job_title', label: 'Job Title' },
      { key: 'company', label: 'Company' },
      { key: 'start_date', label: 'Start Date', type: 'date' },
      { key: 'end_date', label: 'End Date', type: 'date', openLabel: 'Present' },
      { key: 'functions', label: 'Functions' },
    ],
  },
  competencies: {
    label: 'Competencies',
    columns: [
      { key: 'employee_name', label: 'Employee' },
      { key: 'email', label: 'Email' },
      { key: 'job_title', label: 'Job Title' },
      { key: 'name', label: 'Name' },
      { key: 'description', label: 'Description' },
    ],
  },
  certifications: {
    label: 'Certifications',
    columns: [
      { key: 'employee_name', label: 'Employee' },
      { key: 'email', label: 'Email' },
      { key: 'job_title', label: 'Job Title' },
      { key: 'name', label: 'Name' },
      { key: 'governing_organization', label: 'Governing Organization' },
      { key: 'start_date', label: 'Start Date', type: 'date' },
      { key: 'end_date', label: 'End Date', type: 'date', openLabel: 'No expiration' },
    ],
  },
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Guard against CSV/Excel formula injection: a value starting with one of these
// can be interpreted as a formula when opened in a spreadsheet. Prefix with a
// single quote so it is treated as text. (Leading tab/CR can also trigger it.)
const sanitizeForExport = (value) => {
  const s = value == null ? '' : String(value);
  return /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
};

// Export value (clean YYYY-MM-DD strings already come from the server).
const exportValue = (row, col) => sanitizeForExport(row[col.key]);

// Friendlier value for the on-screen table.
const displayValue = (row, col) => {
  const v = row[col.key];
  if (col.type === 'date') {
    if (!v) return col.openLabel || '—';
    const m = String(v).match(/^(\d{4})-(\d{2})/);
    if (m) return `${MONTHS[Number(m[2]) - 1]} ${m[1]}`;
    return String(v);
  }
  return v == null || v === '' ? '—' : String(v);
};

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const exportCsv = (rows, columns, filename) => {
  const escape = (value) => {
    const s = value == null ? '' : String(value);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [columns.map((c) => escape(c.label)).join(',')];
  rows.forEach((row) => {
    lines.push(columns.map((c) => escape(exportValue(row, c))).join(','));
  });
  const blob = new Blob([`﻿${lines.join('\r\n')}`], { type: 'text/csv;charset=utf-8;' });
  downloadBlob(blob, `${filename}.csv`);
};

const exportXlsx = (rows, columns, filename) => {
  const headers = columns.map((c) => c.label);
  const data = rows.map((row) => {
    const obj = {};
    columns.forEach((c) => {
      obj[c.label] = exportValue(row, c);
    });
    return obj;
  });
  const ws = XLSX.utils.json_to_sheet(data, { header: headers });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Resumes');
  XLSX.writeFile(wb, `${filename}.xlsx`);
};

export default function ResumeAdminPage() {
  const navigate = useNavigate();
  const resumesQuery = useAllResumes({ enabled: true });
  const directoryQuery = useCompanyDirectory({ enabled: true });

  const [category, setCategory] = useState('experiences');
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [editEmployeeId, setEditEmployeeId] = useState(null);

  // Every active employee is editable, whether or not they already have resume
  // entries, so managers can start a resume from scratch too.
  const employeeOptions = useMemo(
    () =>
      (directoryQuery.data || [])
        .filter((emp) => emp?.is_active !== false)
        .map((emp) => ({
          label: `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || emp.email || 'Unknown',
          value: emp.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [directoryQuery.data]
  );

  const columns = CATEGORIES[category].columns;
  const allRows = useMemo(() => resumesQuery.data?.[category] || [], [resumesQuery.data, category]);

  const filtered = useMemo(() => {
    const terms = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (terms.length === 0) return allRows;
    return allRows.filter((row) => {
      const haystack = columns.map((c) => String(row[c.key] ?? '')).join(' ').toLowerCase();
      return terms.every((t) => haystack.includes(t));
    });
  }, [allRows, columns, search]);

  const selectedRows = useMemo(
    () => filtered.filter((row) => selectedIds.has(row.id)),
    [filtered, selectedIds]
  );

  // Export the selected rows, or everything currently filtered if none are selected.
  const exportRows = selectedRows.length > 0 ? selectedRows : filtered;

  const changeCategory = (next) => {
    setCategory(next);
    setSelectedIds(new Set());
  };

  const toggleRow = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allFilteredSelected = filtered.length > 0 && filtered.every((row) => selectedIds.has(row.id));

  const toggleSelectAll = () => {
    setSelectedIds(() => (allFilteredSelected ? new Set() : new Set(filtered.map((row) => row.id))));
  };

  const handleExport = (format) => {
    if (exportRows.length === 0) return;
    const filename = `resumes-${category}-${new Date().toISOString().slice(0, 10)}`;
    if (format === 'xlsx') exportXlsx(exportRows, columns, filename);
    else exportCsv(exportRows, columns, filename);
  };

  if (resumesQuery.isLoading) {
    return <LoadingSpinner message="Loading resumes..." />;
  }

  if (resumesQuery.error) {
    return (
      <Message type="error" header="Error" showIcon>
        {String(resumesQuery.error?.message || resumesQuery.error)}
      </Message>
    );
  }

  return (
    <div className="resume-admin bdc-page">
      <PageHeader
        crumbs={[
          { label: 'Home', to: '/' },
          { label: 'Admin', to: '/admin' },
          { label: 'Resumes' },
        ]}
        icon="bi-file-earmark-person"
        title="Manage Resumes"
      />

      <Panel bordered header={<span className="fw-semibold">Edit an employee&rsquo;s resume</span>}>
        <div className="d-flex align-items-end flex-wrap gap-3">
          <div style={{ minWidth: 280 }}>
            <div className="text-muted small mb-1">Employee</div>
            <SelectPicker
              data={employeeOptions}
              value={editEmployeeId}
              onChange={setEditEmployeeId}
              placeholder={directoryQuery.isLoading ? 'Loading…' : 'Select an employee'}
              loading={directoryQuery.isLoading}
              block
            />
          </div>
          <Button
            appearance="primary"
            disabled={!editEmployeeId}
            onClick={() => editEmployeeId && navigate(`/admin/employees/${editEmployeeId}/resume`)}
          >
            <i className="bi bi-pencil me-2" />
            Edit Resume
          </Button>
        </div>
      </Panel>

      <div className="d-flex justify-content-between align-items-end flex-wrap gap-3">
        <ButtonGroup>
          {Object.entries(CATEGORIES).map(([key, cfg]) => (
            <Button
              key={key}
              appearance={category === key ? 'primary' : 'default'}
              onClick={() => changeCategory(key)}
            >
              {cfg.label}
            </Button>
          ))}
        </ButtonGroup>

        <InputGroup inside style={{ width: 340, maxWidth: '100%' }}>
          <Input
            placeholder="Search all fields"
            value={search}
            onChange={setSearch}
          />
          <InputGroup.Addon>
            <i className="bi bi-search" />
          </InputGroup.Addon>
        </InputGroup>
      </div>

      <div className="nsi-table-wrap resume-admin-table">
        <div className="bdc-table-head">
          <div className="bdc-table-head__heading">
            <h3 className="bdc-table-head__title">{CATEGORIES[category].label}</h3>
            <span className="bdc-count">{filtered.length}</span>
            {selectedIds.size > 0 ? (
              <span className="bdc-table-head__note">{selectedRows.length} selected</span>
            ) : null}
          </div>

          <div className="bdc-table-head__actions">
            <button
              type="button"
              className="bdc-btn bdc-btn--sm"
              disabled={exportRows.length === 0}
              onClick={() => handleExport('csv')}
            >
              Export CSV
              <i className="bi bi-filetype-csv" aria-hidden="true" />
            </button>
            <button
              type="button"
              className="bdc-btn bdc-btn--sm bdc-btn--primary"
              disabled={exportRows.length === 0}
              onClick={() => handleExport('xlsx')}
            >
              Export XLSX
              <i className="bi bi-file-earmark-spreadsheet" aria-hidden="true" />
            </button>
          </div>
        </div>

        <table className="nsi-table">
          <thead>
            <tr>
              <th style={{ width: 36 }}>
                <input
                  type="checkbox"
                  checked={allFilteredSelected}
                  onChange={toggleSelectAll}
                  aria-label="Select all"
                />
              </th>
              {columns.map((c) => (
                <th key={c.key}>{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={columns.length + 1} className="nsi-table__empty">
                  No matching resume records.
                </td>
              </tr>
            ) : filtered.map((row) => (
              <tr key={row.id}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(row.id)}
                    onChange={() => toggleRow(row.id)}
                    aria-label="Select row"
                  />
                </td>
                {columns.map((c) => (
                  <td key={c.key} className={c.key === 'functions' || c.key === 'description' ? 'resume-admin-wide' : undefined}>
                    {displayValue(row, c)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
